// ── Enhanced IR + water vapour for the rapid (nowCOAST) GOES feed ──
// nowCOAST serves longwave IR as 8-bit greyscale (cold = bright) and forbids
// server-side restyling (SLD_BODY → 403). A MapLibre custom protocol recolours
// each tile in the browser: grey level → cloud-top brightness temperature →
// the SAME colour table as NASA GIBS's Band-13 layer, so the 4-minute-old
// rapid feed and the 40-minute-old full disk read identically on the map.
//
// Calibration (2026-09-23): nowCOAST grey vs GIBS Band-13 over 700k co-located
// pixels (Hurricane Polo scene, 19:00Z), matched by quantiles (both fields are
// monotone in temperature, so quantile matching is immune to small parallax /
// scan-time offsets). Accuracy ~±2 °C in the −20…−80 °C range that matters.

import maplibregl from "maplibre-gl";

// grey level → °C control points (linear between; extrapolated at the ends)
const GREY_TO_C: [number, number][] = [
  [30, 47.6], [40, 35.4], [50, 29.9], [60, 24.6], [70, 21.6], [80, 18.1], [90, 14.2],
  [100, 10.7], [110, 6.2], [120, 2.1], [130, -2.4], [140, -6.8], [150, -11.3], [160, -16.6],
  [170, -24.4], [180, -30.9], [190, -37.6], [200, -44.6], [210, -53.6], [220, -65.6],
  [230, -76.6], [240, -87.6], [255, -104],
];

// GIBS "Clean_Longwave_Infrared_Window_Band" colour table at 1 °C, −92…+57 °C
const GIBS_B13_HEX =
  "ffffff7f007f8c0d8799198ea52696b2339dbf40a5cc4cadd959b4e566bcf272c3ff7fcbe6e6e6ccccccb1b1b19b9b9b8181816666664c4c4c3636361b1b1b050505" +
  "1a00003300004d0000660000800000990000b30000cc0000e60000ff0000ff1a00ff3300ff4d00ff6600ff8000ff9900ffb300ffcc00ffe600ffff00e6ff00ccff00" +
  "b3ff0099ff0080ff0066ff004dff0033ff001aff0000ff0000ea0a00d41300bf1d00aa2600953000803a006a4300554d004056002a60000073000d7a0026880040960059a4" +
  "0073b2008cc000a6ce00bfdc00d9ea00f2f8c5c5c5c2c2c2c0c0c0bdbdbdbbbbbbb8b8b8b5b5b5b3b3b3b0b0b0aeaeaeabababa9a9a9a6a6a6a3a3a3a1a1a19e9e9e9c9c9c" +
  "9999999696969494949191918f8f8f8c8c8c8a8a8a8787878484848282827f7f7f7d7d7d7a7a7a7777777575757272727070706d6d6d6a6a6a6868686565656363636060605e5e5e" +
  "5b5b5b5858585656565353535151514e4e4e4b4b4b4949494646464444444141413e3e3e3c3c3c3939393737373434343232322f2f2f2c2c2c2a2a2a2727272525252222221f1f1f" +
  "1d1d1d1a1a1a1818181515151313131010100d0d0d0b0b0b080808060606030303010101";
const CMAP_MIN_C = -92;

// 256-entry RGB lookup: grey → colour (opaque, exactly like the GIBS layer)
const LUT_IR = (() => {
  const lut = new Uint8ClampedArray(256 * 3);
  const n = GIBS_B13_HEX.length / 6;
  for (let g = 0; g < 256; g++) {
    const i = Math.min(n - 1, Math.max(0, Math.round(interp(GREY_TO_C, g) - CMAP_MIN_C)));
    for (let k = 0; k < 3; k++) lut[g * 3 + k] = parseInt(GIBS_B13_HEX.slice(i * 6 + k * 2, i * 6 + k * 2 + 2), 16);
  }
  return lut;
})();

// ── Clear sky see-through ──
// In IR, clear land and sea read as warm surfaces, and the Band-13 table
// paints them a flat mid-grey that hides the map under the overlay. Warmer
// than IR_SOLID_C the picture fades, gone by IR_CLEAR_C: the map shows
// through clear sky while every cloud keeps its colour (cold ground, like
// land on a winter night, still shows as it does in IR).
export const IR_SOLID_C = -10;
export const IR_CLEAR_C = 12; // night-time land and warm haze (~+12…+20 °C) were most of the grey wash
const fade = (c: number) => Math.round(255 * Math.min(1, Math.max(0, (IR_CLEAR_C - c) / (IR_CLEAR_C - IR_SOLID_C))));
const ALPHA_IR = (() => {
  const a = new Uint8ClampedArray(256);
  for (let g = 0; g < 256; g++) a[g] = fade(interp(GREY_TO_C, g));
  return a;
})();

// GIBS's full-disk IR tiles come already coloured: its greys run from
// −21 °C (197) to +57 °C (1), close to linear. But its table also has a grey
// band for −71…−80 °C (the coldest tops, between the reds and the purples),
// and its greys are finer than any table we can match, so a grey alone can't
// say which it is. Each connected patch of grey is judged by what it touches:
// the cold band sits against reds and purples (opaque, as drawn), clear sky
// against the cyan and blue of cloud edges, or nothing at all (faded).
const ALPHA_GIBS_GREY = (() => {
  const a = new Uint8ClampedArray(256);
  for (let g = 0; g < 256; g++) a[g] = fade(-21 + ((197 - g) * 78) / 196);
  return a;
})();
const GREY_TOL = 24; // max − min channel at most this: a grey

export function gibsClearSky(d: Uint8ClampedArray, w: number, h: number): void {
  const n = w * h;
  // 1 = grey, 2 = a colder colour (reds, purples: R > G), 3 = a cloud-edge colour (cyan, blue, green)
  const cls = new Uint8Array(n);
  for (let p = 0, i = 0; p < n; p++, i += 4) {
    if (d[i + 3] === 0) continue;
    const r = d[i], g = d[i + 1], b = d[i + 2];
    cls[p] = Math.max(r, g, b) - Math.min(r, g, b) <= GREY_TOL ? 1 : r > g ? 2 : 3;
  }
  const seen = new Uint8Array(n);
  const patch = new Int32Array(n);
  for (let s0 = 0; s0 < n; s0++) {
    if (cls[s0] !== 1 || seen[s0]) continue;
    let head = 0, tail = 0, cold = 0, edge = 0;
    patch[tail++] = s0;
    seen[s0] = 1;
    while (head < tail) {
      const p = patch[head++];
      const x = p % w;
      for (let k = 0; k < 4; k++) { // left, right, up, down
        const q = k === 0 ? (x > 0 ? p - 1 : -1) : k === 1 ? (x < w - 1 ? p + 1 : -1) : k === 2 ? p - w : p + w;
        if (q < 0 || q >= n) continue;
        if (cls[q] === 1) {
          if (!seen[q]) { seen[q] = 1; patch[tail++] = q; }
        } else if (cls[q] === 2) cold++;
        else if (cls[q] === 3) edge++;
      }
    }
    if (cold > edge) continue; // the −71…−80 °C band: keep it
    for (let j = 0; j < tail; j++) {
      const i = patch[j] * 4;
      d[i + 3] = Math.min(d[i + 3], ALPHA_GIBS_GREY[Math.round((d[i] + d[i + 1] + d[i + 2]) / 3)]);
    }
  }
}

// Water vapour: nowCOAST's layer is GOES band 8 (upper-level, 6.2 µm) —
// Spearman ρ = −0.997 against band-8 brightness temperature (vs 0.976 for
// band 9), calibrated the same way as the IR (2026-09-23, 407k pixels).
const GREY_TO_C_WV: [number, number][] = [
  [0, -22.9], [10, -24.2], [20, -25.5], [30, -26.8], [40, -28.1], [50, -29.5], [60, -31.1], [70, -32.5],
  [80, -34.1], [90, -35.8], [100, -37.7], [110, -39.6], [120, -41.7], [130, -44.0], [140, -46.5], [150, -49.0],
  [160, -52.8], [170, -55.7], [180, -59.7], [190, -63.8], [200, -68.0], [255, -90],
];
// The global mosaic (NOAA/NESDIS GMGSI, ~6.7 µm, nowCOAST
// "global_water_vapor_imagery_mosaic") that fills in outside the GOES
// sectors. Its own style has no colour map, so GeoServer contrast-stretches
// every tile differently (a blocky patchwork); the tiles are requested with
// the server's fixed "goes-lir" ramp instead (grey = 255 − raw, moist = dark).
// Quantile-matched on that ramp to the band-8 layer above over 845k
// co-located pixels × 3 hours (2026-09-23 20–22Z, 16–48°N): Spearman −0.988,
// mapped pixels land a median 0.2 °C from band 8 (96 % within 3 °C), so the
// seam between the two layers doesn't show.
const GREY_TO_C_GWV: [number, number][] = [
  [0, -90], [40, -69.2], [45, -65.2], [50, -62.2], [55, -55.7], [60, -50.5], [65, -47.0], [70, -41.3],
  [75, -35.8], [80, -31.2], [85, -28.8], [90, -26.1], [95, -23.5], [100, -22.9],
];
// °C, coldest first. Dry air near-black, turning brown → gold over its
// driest 2 °C (the feed resolves down to ~−23 °C); moist air grey → white →
// blue; the coldest tops navy → red → orange → yellow over their last few
// degrees (the rapid feed bottoms out near −76 °C), so a convective burst is
// a compact hot core, as dry slots and intrusions are dark against moist
// air. Tuned on the 2026-09-30 11Z scene (Baja / SW US dry air, Hurricane
// Rachel). Keep in sync with _WV_STOPS in data/goes_meso.py (the 1-minute loop).
export const WV_STOPS: [number, [number, number, number]][] = [
  [-90, [255, 250, 200]], [-80, [255, 230, 90]], [-77, [255, 165, 35]], [-74.5, [245, 95, 25]],
  [-72, [200, 25, 40]], [-69, [70, 20, 100]], [-65, [20, 30, 120]], [-60, [30, 60, 175]],
  [-55, [40, 105, 215]], [-50, [120, 175, 240]], [-46.5, [205, 225, 250]], [-43, [245, 245, 245]],
  [-39, [200, 200, 200]], [-35.5, [135, 135, 135]], [-32.5, [70, 68, 66]], [-30, [34, 30, 26]],
  [-26.5, [26, 20, 14]], [-25, [65, 46, 18]], [-24, [115, 85, 32]], [-23, [175, 145, 62]],
  [-18, [230, 195, 100]],
];
function wvLut(greyToC: [number, number][]) {
  const lut = new Uint8ClampedArray(256 * 3);
  for (let g = 0; g < 256; g++) {
    const c = interp(greyToC, g);
    for (let k = 0; k < 3; k++) {
      lut[g * 3 + k] = Math.round(interp(WV_STOPS.map(([t, rgb]) => [t, rgb[k]] as [number, number]), c));
    }
  }
  return lut;
}
const LUT_WV = wvLut(GREY_TO_C_WV);
const LUT_GWV = wvLut(GREY_TO_C_GWV);

/** piecewise-linear lookup over sorted [x, y] points, clamped at the ends */
function interp(pts: [number, number][], x: number): number {
  if (x <= pts[0][0]) return pts[0][1];
  for (let i = 1; i < pts.length; i++) {
    if (x <= pts[i][0]) {
      const [x0, y0] = pts[i - 1], [x1, y1] = pts[i];
      return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
    }
  }
  return pts[pts.length - 1][1];
}

const SCHEMES = { enhir: LUT_IR, enhwv: LUT_WV, enhgwv: LUT_GWV } as const;
let registered = false;

/** Wrap an https tile URL so it is fetched through the recolouring protocol. */
export function enhancedIrUrl(httpsUrl: string): string {
  return httpsUrl.replace(/^https:\/\//, "enhir://");
}
export function enhancedWvUrl(httpsUrl: string): string {
  return httpsUrl.replace(/^https:\/\//, "enhwv://");
}
export function enhancedGlobalWvUrl(httpsUrl: string): string {
  return httpsUrl.replace(/^https:\/\//, "enhgwv://");
}
/** GIBS full-disk IR with clear sky see-through. */
export function gibsIrUrl(httpsUrl: string): string {
  return httpsUrl.replace(/^https:\/\//, "gibsir://");
}

/** Register once per page (idempotent). */
export function registerEnhancedIr() {
  if (registered) return;
  registered = true;
  for (const [scheme, LUT] of Object.entries(SCHEMES)) {
    registerScheme(scheme, LUT, scheme === "enhgwv" ? goesCoverage : undefined, scheme === "enhir" ? ALPHA_IR : undefined);
  }
  maplibregl.addProtocol("gibsir", async (params, abort) => {
    const res = await fetch(params.url.replace("gibsir://", "https://"), { signal: abort.signal });
    if (!res.ok) throw new Error(`GIBS IR tile ${res.status}`);
    const bmp = await createImageBitmap(await res.blob());
    const canvas = new OffscreenCanvas(bmp.width, bmp.height);
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    ctx.drawImage(bmp, 0, 0);
    bmp.close();
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    gibsClearSky(img.data, canvas.width, canvas.height);
    ctx.putImageData(img, 0, 0);
    return { data: await createImageBitmap(canvas) };
  });
}

// ── the global base stays clear of the GOES sectors ──
// Both water-vapour layers draw at the overlay opacity, so where the global
// mosaic sat UNDER the GOES layer the basemap showed through less — a
// brightness step along the sector edge (measured +10 of 255 at 85 %
// opacity, +18 at 60 %). The base therefore takes the GOES layer's own
// alpha for the same tile as a cut-out mask, and every pixel is painted by
// exactly one layer. The sectors don't move, so the newest frame's alpha
// serves every frame: fetched once per tile.
type Mask = "none" | "all" | Uint8Array;
const masks = new Map<string, Promise<Mask>>();

function goesCoverage(baseUrl: string): Promise<Mask> {
  const url = baseUrl
    .replace(/layers=[^&]+/, "layers=goes_water_vapor_imagery")
    .replace(/styles=[^&]*/, "styles=")
    .replace(/&time=[^&]*/, "");
  let p = masks.get(url);
  if (!p) {
    p = (async (): Promise<Mask> => {
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(`GOES mask ${res.status}`);
        const d = await pixels(await res.blob());
        const a = new Uint8Array(d.length / 4);
        let any = false, full = true;
        for (let i = 0; i < a.length; i++) {
          a[i] = d[i * 4 + 3];
          if (a[i]) any = true;
          if (a[i] !== 255) full = false;
        }
        return !any ? "none" : full ? "all" : a;
      } catch {
        masks.delete(url); // retry with the next tile request
        return "none"; // worst case: this tile stacks, as before
      }
    })();
    masks.set(url, p);
    if (masks.size > 400) masks.delete(masks.keys().next().value!);
  }
  return p;
}

async function pixels(blob: Blob): Promise<Uint8ClampedArray> {
  const bmp = await createImageBitmap(blob);
  const canvas = new OffscreenCanvas(bmp.width, bmp.height);
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.drawImage(bmp, 0, 0);
  bmp.close();
  return ctx.getImageData(0, 0, canvas.width, canvas.height).data;
}

function registerScheme(scheme: string, LUT: Uint8ClampedArray, mask?: (url: string) => Promise<Mask>, alpha?: Uint8ClampedArray) {
  maplibregl.addProtocol(scheme, async (params, abort) => {
    const url = params.url.replace(`${scheme}://`, "https://");
    const [res, m] = await Promise.all([fetch(url, { signal: abort.signal }), mask?.(url)]);
    if (!res.ok) throw new Error(`enhanced IR tile ${res.status}`);
    const bmp = await createImageBitmap(await res.blob());
    const canvas = new OffscreenCanvas(bmp.width, bmp.height);
    const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
    if (m === "all") {
      bmp.close();
      return { data: await createImageBitmap(canvas) }; // wholly under the GOES layer
    }
    ctx.drawImage(bmp, 0, 0);
    bmp.close();
    const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const d = img.data;
    for (let i = 0; i < d.length; i += 4) {
      if (d[i + 3] === 0) continue; // outside the sector
      const v = d[i], g = v * 3; // greyscale: R = G = B
      d[i] = LUT[g]; d[i + 1] = LUT[g + 1]; d[i + 2] = LUT[g + 2];
      if (alpha) d[i + 3] = Math.min(d[i + 3], alpha[v]);
    }
    if (m instanceof Uint8Array && m.length === d.length / 4) {
      for (let p = 0; p < m.length; p++) if (m[p]) d[p * 4 + 3] = Math.min(d[p * 4 + 3], 255 - m[p]);
    }
    ctx.putImageData(img, 0, 0);
    // MapLibre accepts an ImageBitmap straight from a protocol handler
    return { data: await createImageBitmap(canvas) };
  });
}
